import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, doc, updateDoc, deleteDoc, orderBy, query } from "firebase/firestore";
import toast from "react-hot-toast";

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "users"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch { toast.error("Failed to load members."); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMembers(); }, []);

  const approve = async (id) => {
    await updateDoc(doc(db, "users", id), { status: "approved" });
    toast.success("Member approved.");
    fetchMembers();
  };

  const reject = async (id) => {
    await updateDoc(doc(db, "users", id), { status: "rejected" });
    toast.success("Member rejected.");
    fetchMembers();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this member permanently?")) return;
    await deleteDoc(doc(db, "users", id));
    toast.success("Member deleted.");
    fetchMembers();
  };

  const filtered = members.filter(m => filter === "all" ? true : m.status === filter);
  const counts = { pending: members.filter(m => m.status === "pending").length, approved: members.filter(m => m.status === "approved").length, rejected: members.filter(m => m.status === "rejected").length };

  const statusColor = { pending: "#f5c518", approved: "#4ade80", rejected: "#f87171" };

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 6 }}>Admin</div>
        <h2 style={{ fontSize: "1.5rem" }}>Members</h2>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 28 }}>
        {[
          { label: "Pending", count: counts.pending, color: "#f5c518" },
          { label: "Approved", count: counts.approved, color: "#4ade80" },
          { label: "Rejected", count: counts.rejected, color: "#f87171" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "20px 24px", borderTop: `2px solid ${s.color}` }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.16em", textTransform: "uppercase", color: s.color, marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "2rem", color: "#f0f0f0" }}>{s.count}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {["pending", "approved", "rejected", "all"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.12em", textTransform: "uppercase", padding: "6px 14px", borderRadius: "20px", border: `1px solid ${filter === f ? statusColor[f] || "#c9a84c" : "var(--border)"}`, background: filter === f ? (statusColor[f] || "#c9a84c") + "15" : "transparent", color: filter === f ? (statusColor[f] || "#c9a84c") : "var(--text-muted)", cursor: "pointer", transition: "all 0.2s" }}>
            {f}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-muted)" }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "12px" }}>No members found.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(m => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 16, background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "16px 20px", flexWrap: "wrap" }}>
              {/* Status dot */}
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: statusColor[m.status] || "#555", boxShadow: `0 0 8px ${statusColor[m.status] || "#555"}80`, flexShrink: 0 }} />

              {/* Info */}
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 600, fontSize: "14px", color: "var(--text-primary)", marginBottom: 2 }}>{m.name || "No name"}</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>{m.email}</div>
              </div>

              {/* Status badge */}
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", padding: "3px 10px", borderRadius: "20px", background: (statusColor[m.status] || "#555") + "15", border: `1px solid ${(statusColor[m.status] || "#555")}30`, color: statusColor[m.status] || "#555", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                {m.status}
              </span>

              {/* Date */}
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)" }}>
                {m.createdAt?.toDate ? m.createdAt.toDate().toLocaleDateString() : ""}
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                {m.status === "pending" && (
                  <>
                    <button onClick={() => approve(m.id)} style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", padding: "6px 14px", background: "#4ade8015", border: "1px solid #4ade8030", borderRadius: "6px", color: "#4ade80", cursor: "pointer", transition: "all 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#4ade8025"}
                      onMouseLeave={e => e.currentTarget.style.background = "#4ade8015"}
                    >Approve</button>
                    <button onClick={() => reject(m.id)} style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", padding: "6px 14px", background: "#f8717115", border: "1px solid #f8717130", borderRadius: "6px", color: "#f87171", cursor: "pointer", transition: "all 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8717125"}
                      onMouseLeave={e => e.currentTarget.style.background = "#f8717115"}
                    >Reject</button>
                  </>
                )}
                {m.status === "rejected" && (
                  <button onClick={() => approve(m.id)} style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", padding: "6px 14px", background: "#4ade8015", border: "1px solid #4ade8030", borderRadius: "6px", color: "#4ade80", cursor: "pointer" }}>Approve</button>
                )}
                {m.status === "approved" && (
                  <button onClick={() => reject(m.id)} style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", padding: "6px 14px", background: "#f5c51815", border: "1px solid #f5c51830", borderRadius: "6px", color: "#f5c518", cursor: "pointer" }}>Revoke</button>
                )}
                <button onClick={() => remove(m.id)} style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", padding: "6px 12px", background: "transparent", border: "1px solid #1e1e1e", borderRadius: "6px", color: "#333", cursor: "pointer", transition: "all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.color = "#f87171"; e.currentTarget.style.borderColor = "#f8717130"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "#333"; e.currentTarget.style.borderColor = "#1e1e1e"; }}
                >Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
